/**
 * Contact-contractor drawer, opened from a Priority-queue row.
 *
 * Same skeleton as the golden contractor drawer (`design-rules/03-organisms.md`): `Modal` +
 * `ModalHeader`, 920 = 400 left + 520 right, left panel `justify-between` so the action zone pins
 * to the bottom. What differs is the payload — this drawer answers "who do I call and what do I
 * say", not "everything about this contractor".
 *
 * Left: identity + reach-out details (`DataRow`, copy affordances) over the log-a-contact form.
 * Right: two tabs, as in the sketch. General shows the latest contact and the documents that put
 * this person in the queue, rendered with the shared `ContractorStepDetailsCard` so submissions
 * look the same everywhere; External contacts shows the whole outreach history.
 *
 * `detail` (identity, documents, full contact history) comes from `GET .../priority-queue/:id`,
 * fetched fresh by the parent every time the drawer opens — see `PriorityQueue.tsx`.
 */
import { Fragment, memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { DataRow } from 'src/components/shared/DataRow'
import { ContractorStepDetailsCard } from 'src/components/contractorStepDetailsCard/ContractorStepDetailsCard'
import {
  ContractorStepDetailsCardStatus,
  ContractorStepDetailsCardUsageVariant,
} from 'src/components/contractorStepDetailsCard/constants'
import { SubmissionBlock } from 'src/components/contractorStepDetailsCard/types'
import { ContractorStatusChip } from 'src/components/contractor-list/shared/ContractorStatusChip'
import { ContractorTypeChip } from 'src/components/contractor-list/shared/ContractorTypeChip'
import { ContactDrawerTabs } from './ContactDrawerTabs'
import { EmptyContacts } from './EmptyContacts'
import { ContactHistoryItem } from './ContactHistoryItem'
import { LogContactForm } from './LogContactForm'
import { getDaysAgo, getDaysLeft, getDisplayStatus } from './utils'
import { ContactChannel, ContactDrawerTab, PriorityQueueDetail } from './types'
import { mapCollectedDataStatusToCardStatus } from '../contractorStepDetailsCard/utils'

interface Props {
  detail: PriorityQueueDetail | null
  onLogContact(contractorId: string, channel: ContactChannel, note?: string): Promise<void>
  onClose(): void
}

const ContactContractorDrawerComponent = ({ detail, onLogContact, onClose }: Props) => {
  const [tab, setTab] = useState(ContactDrawerTab.General)

  const handleLogged = useCallback(
    async (channel: ContactChannel, note: string) => {
      if (!detail) {
        return
      }

      await onLogContact(detail.id, channel, note || undefined)
    },
    [detail, onLogContact],
  )

  const documents = useMemo(() => {
    return (
      detail?.expiringDocuments.map((document) => {
        return {
          collectedData: document,
          daysLeft: getDaysLeft(document.expiresAt ?? ''),
        }
      }) ?? []
    )
  }, [detail])

  const contacts = useMemo(() => {
    return (
      detail?.contacts.map((contact) => {
        return {
          channel: contact.channel,
          daysAgo: getDaysAgo(contact.createdAt),
          by: contact.author.name,
          note: contact.note,
        }
      }) ?? []
    )
  }, [detail])

  if (!detail) {
    return null
  }

  return (
    <Modal open onClose={onClose} css={tw`flex flex-row w-full h-full`}>
      {/* A `Fragment`, exactly as `ContractorDetailsModal` does it: any wrapper that spans the
          viewport swallows backdrop clicks and the drawer stops closing on the dimmed area. */}
      <Fragment>
        <div
          data-testid="contact-contractor-drawer"
          css={tw`w-[920px] h-full bg-white flex flex-col`}
        >
          <ModalHeader
            title="Contact contractor"
            onClose={onClose}
            className={tw`text-black h-[48px]`}
            closeButtonDataTestId="contact-contractor-drawer-close-btn"
          />

          <div css={tw`flex h-[calc(100vh-48px)]`}>
            <div
              css={tw`max-w-[400px] flex-1 flex flex-col justify-between overflow-y-auto border-0 border-r border-solid border-r-[#EDEDED]`}
            >
              <div css={tw`flex p-4`}>
                <div css={tw`w-full`}>
                  <div>
                    <Typography variant="h2">{detail.name}</Typography>
                    <DataRow className={tw`pt-1`} title={detail.contractorId} />
                  </div>
                  <DataRow
                    title="Contractor status"
                    direction="row"
                    component={<ContractorStatusChip status={getDisplayStatus(detail.status)} />}
                  />
                  <DataRow
                    title="Contractor type"
                    direction="row"
                    component={<ContractorTypeChip type={detail.contractorType} />}
                  />
                  {/* Stacked, not side-by-side: contractor details renders this row with an
                      `onEdit`, which forces `DataRow` into its column layout. Editing an external
                      ID is out of scope here, so `direction` reproduces that shape directly.
                      `value` is what the copy button writes to the clipboard. */}
                  <DataRow
                    direction="column"
                    title="External ID"
                    text={detail.contractorId}
                    value={detail.contractorId}
                    showCopyButton
                  />
                  <DataRow direction="row" title="Phone number" text={detail.phoneNumber} />
                  <DataRow direction="row" title="Email" text={detail.email} />
                </div>
              </div>

              <LogContactForm contractorName={detail.name} onLogged={handleLogged} />
            </div>

            <div css={tw`w-[520px] flex-1 flex flex-col min-h-0`}>
              <ContactDrawerTabs selectedTab={tab} onClick={setTab} />

              <div css={tw`flex-1 bg-[#F6F6F6] overflow-y-auto p-4 pb-20`}>
                {tab === ContactDrawerTab.General ? (
                  <div>
                    <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-2`}>
                      Last external contact
                    </Typography>

                    <div css={tw`mb-6`}>
                      {contacts.length ? <ContactHistoryItem contact={contacts[0]} /> : null}
                      {contacts.length ? null : <EmptyContacts />}
                    </div>

                    <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-2`}>
                      Expiring documents
                    </Typography>

                    <div css={tw`flex flex-col gap-2`}>
                      {documents.map(({ collectedData, daysLeft }) => {
                        const status =
                          daysLeft < 0
                            ? ContractorStepDetailsCardStatus.Expired
                            : mapCollectedDataStatusToCardStatus(collectedData.status)

                        const blocks: SubmissionBlock[] = [
                          {
                            status,
                            collectedData,
                            expiresAt: dayjs(collectedData.expiresAt).format('MM/DD/YYYY'),
                            showExpirationDate: true,
                            showEffectiveDate: false,
                          },
                        ]

                        return (
                          <ContractorStepDetailsCard
                            key={collectedData.id}
                            variant={ContractorStepDetailsCardUsageVariant.Contractor}
                            status={status}
                            collectedData={collectedData}
                            contentBlocks={blocks}
                            readOnly
                          />
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div css={tw`flex flex-col gap-2`}>
                    {contacts.map((contact, index) => {
                      return (
                        <ContactHistoryItem
                          key={`${contact.channel}-${contact.daysAgo}-${index}`}
                          contact={contact}
                        />
                      )
                    })}
                    {contacts.length ? null : <EmptyContacts />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    </Modal>
  )
}

export const ContactContractorDrawer = memo(ContactContractorDrawerComponent)
